
import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { detectLocale, SITE_URL } from "@/lib/locale";
import { OG_IMAGE_URL, OG_IMAGE_ALT, OG_IMAGE_TYPE, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from "@/lib/seo-pages";
import { LazyClientChrome } from "@/components/calv/LazyClientChrome";
import { RedirectHandler } from "@/components/RedirectHandler";
import { usePageTracking } from "@/hooks/usePageTracking";
import { PostHogTracker } from "@/components/calv/PostHogTracker";

import appCss from "../styles.css?url";
import jakartaFontUrl from "@/assets/fonts/PlusJakartaSans.woff2?url";
import jetbrainsFontUrl from "@/assets/fonts/JetBrainsMono.woff2?url";

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Kalvarienberg 20",
  addressLocality: "Karlstadt am Main",
  postalCode: "97753",
  addressRegion: "Bayern",
  addressCountry: "DE",
};

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Calvarius GmbH",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  image: OG_IMAGE_URL,
  description:
    "Digitalagentur seit 2013. Online-Marketing wirtschaftlich gedacht — SEO, SEA, CRO, Tracking und KI als integriertes System.",
  foundingDate: "2013",
  address: POSTAL_ADDRESS,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "info@calvarius.com",
      telephone: "+49-9353-9768000",
      availableLanguage: ["de", "en"],
    },
  ],
  sameAs: ["https://linkedin.com/company/calvarius"],
};

const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Calvarius",
  description:
    "Performance-Marketing-Agentur aus Karlstadt am Main. KMU-Schwerpunkt im E-Commerce, plus B2B und SaaS. Google Ads, Meta Ads, LinkedIn Ads, CRO, SEO und Tracking-Architektur.",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  telephone: "+49-9353-9768000",
  email: "info@calvarius.com",
  address: POSTAL_ADDRESS,
  geo: {
    "@type": "GeoCoordinates",
    latitude: "49.9603",
    longitude: "9.7727",
  },
  areaServed: [
    { "@type": "City", name: "Karlstadt am Main" },
    { "@type": "City", name: "Würzburg" },
    { "@type": "City", name: "Schweinfurt" },
    { "@type": "City", name: "Aschaffenburg" },
    { "@type": "City", name: "Frankfurt am Main" },
    { "@type": "City", name: "Nürnberg" },
    { "@type": "AdministrativeArea", name: "Bayern" },
    { "@type": "AdministrativeArea", name: "Hessen" },
    { "@type": "Country", name: "Deutschland" },
  ],
  founder: { "@type": "Person", name: "André Freier" },
  foundingDate: "2013",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  sameAs: ["https://linkedin.com/company/calvarius"],
};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Calvarius",
  url: SITE_URL,
  inLanguage: ["de-DE", "en-US"],
};

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Calvarius GmbH" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "theme-color", content: "#0B1B2A" },
      { property: "og:site_name", content: "Calvarius" },
      { property: "og:image:width", content: OG_IMAGE_WIDTH },
      { property: "og:image:height", content: OG_IMAGE_HEIGHT },
      { property: "og:image:type", content: OG_IMAGE_TYPE },
      { property: "og:image:alt", content: OG_IMAGE_ALT },
      { title: "calvarius GmbH - Performance Marketing & Digitale Dienste" },
      { property: "og:title", content: "calvarius GmbH - Performance Marketing & Digitale Dienste" },
      { name: "twitter:title", content: "calvarius GmbH - Performance Marketing & Digitale Dienste" },
      { name: "description", content: "calvarius bietet Performance Marketing & Digitale Dienste für Werbetreibende Unternehmen an" },
      { property: "og:description", content: "calvarius bietet Performance Marketing & Digitale Dienste für Werbetreibende Unternehmen an" },
      { name: "twitter:description", content: "calvarius bietet Performance Marketing & Digitale Dienste für Werbetreibende Unternehmen an" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:secure_url", content: OG_IMAGE_URL },
      { name: "twitter:image", content: OG_IMAGE_URL },
      { name: "twitter:image:alt", content: OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "shortcut icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "stylesheet", href: appCss },
      { rel: "preload", as: "font", type: "font/woff2", href: jakartaFontUrl, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: jetbrainsFontUrl, crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(ORG_JSONLD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(LOCAL_BUSINESS_JSONLD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(WEBSITE_JSONLD),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // Lang attribute must come from the router state on both server and client
  // to avoid hydration mismatches on /en/* routes.
  const path = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(path);
  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootInner() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(path);
  usePageTracking();
  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-ink focus:px-4 focus:py-2 focus:font-mono focus:text-xs focus:uppercase focus:tracking-wider focus:text-white"
      >
        {locale === "de" ? "Zum Inhalt springen" : "Skip to content"}
      </a>
      <RedirectHandler />
      <PostHogTracker />
      <Outlet />
      <LazyClientChrome />
    </>
  );
}

function RootComponent() {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <RootInner />
    </QueryClientProvider>
  );
}
